import { startTransition, useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { buildPostQueryMlLensEnvelope } from '../../common/utils/events/build-post-query-ml-lens-envelope';
import { TASK_NAMES } from '../../common/utils/events/task-names';
import {
	onTaskAbort,
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
} from '../../common/utils/events/vital-stats-events';
import { getProductResultCounts } from '../../common/utils/quick-find/analytics';
import { useAnalytics } from '../analytics';
import { useCurrentUserId } from '../store/bootstrap';

import { useQuickFindAttributes } from './analytics';
import { useVitalStatsTaskState } from './quickfind-post-query-vitalstats-context';

type AbortReason =
	| 'dialog_closed_during_network'
	| 'superseded_by_new_query'
	| 'dialog_closed_before_debounce_fired'
	| 'text_cleared';

/**
 * Hook to manage VitalStats events for quickfind post-query UX task lifecycle.
 * Tracks task state and provides methods to fire appropriate VitalStats events.
 * Uses shared context to maintain state across multiple hook instances.
 */
export const useQuickFindPostQueryVitalStats: () => {
	startTask: () => void;
	completeTaskSuccess: () => void;
	completeTaskFail: (error?: Error) => void;
	abortTask: (abortReason: AbortReason) => void;
	isTaskActive: () => boolean;
} = () => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useQuickFindAttributes();
	const taskStateRef = useVitalStatsTaskState();
	const userId = useCurrentUserId();

	const isEnabled = fg('rovo-search-vitalstats-quickfind-postquery-ux');

	const startTask = useCallback((): void => {
		if (!isEnabled) {
			return;
		}

		const taskId = crypto.randomUUID();
		const searchSessionId = commonAttributes.searchSessionId || '';

		// Set the shared context state
		if (taskStateRef.current) {
			taskStateRef.current.id = taskId;
			taskStateRef.current.started = true;
			taskStateRef.current.completed = false;
		}

		fireAnalyticsEvent(
			onTaskStart({
				searchSessionId,
				taskId,
				task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
			}),
		);
	}, [isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef]);

	const completeTaskSuccess = useCallback((): void => {
		// Check if task is active using the shared context state
		if (!isEnabled || !taskStateRef.current?.started || taskStateRef.current?.completed) {
			return;
		}

		const { id: taskId } = taskStateRef.current;
		const searchSessionId = commonAttributes.searchSessionId || '';

		// taskId is guaranteed to be non-null when started is true
		if (!taskId) {
			return;
		}

		// Additive `ml_lens` envelope so the ML Lens transformer can split this
		// taskSuccess into per-connector `results_per_connector` metrics.
		// Gated independently of the parent VitalStats FG; on `POSTQUERY_NO_RESULTS`
		// `getProductResultCounts` returns `{}` and the envelope is emitted with
		// `metrics: []`.
		const moreContext = fg('rovo-search-ml-lens')
			? { ml_lens: buildPostQueryMlLensEnvelope(getProductResultCounts(), userId) }
			: undefined;

		fireAnalyticsEvent(
			onTaskSuccess({
				searchSessionId,
				taskId,
				task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
				moreContext,
			}),
		);

		// Mark task as completed
		if (taskStateRef.current) {
			taskStateRef.current.completed = true;
		}
	}, [isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef, userId]);

	const completeTaskFail = useCallback(
		(error?: Error): void => {
			// Check if task is active using the shared context state
			if (!isEnabled || !taskStateRef.current?.started || taskStateRef.current?.completed) {
				return;
			}

			const { id: taskId } = taskStateRef.current;
			const searchSessionId = commonAttributes.searchSessionId || '';

			// taskId is guaranteed to be non-null when started is true
			if (!taskId) {
				return;
			}

			fireAnalyticsEvent(
				onTaskFail({
					searchSessionId,
					taskId,
					task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
					moreContext: {
						errorName: error?.name,
						errorMessage: error?.message,
					},
				}),
			);

			// Mark task as completed
			taskStateRef.current.completed = true;
		},
		[isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef],
	);

	const abortTask = useCallback(
		(abortReason: AbortReason): void => {
			// Check if task is active using the shared context state
			if (!isEnabled || !taskStateRef.current?.started || taskStateRef.current?.completed) {
				return;
			}

			const { id: taskId } = taskStateRef.current;
			const searchSessionId = commonAttributes.searchSessionId || '';

			// taskId is guaranteed to be non-null when started is true
			if (!taskId) {
				return;
			}

			// Mark task as completed FIRST (synchronously) to prevent race conditions
			taskStateRef.current.completed = true;

			// Then defer the analytics event to not block rendering
			startTransition(() => {
				fireAnalyticsEvent(
					onTaskAbort({
						searchSessionId,
						taskId,
						task: TASK_NAMES.QUICK_FIND_POST_QUERY_UX,
						moreContext: {
							abortReason,
						},
					}),
				);
			});
		},
		[isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef],
	);

	const isTaskActive = useCallback((): boolean => {
		// Check if task has started but not yet completed
		return taskStateRef.current?.started && !taskStateRef.current?.completed;
	}, [taskStateRef]);

	return {
		startTask,
		completeTaskSuccess,
		completeTaskFail,
		abortTask,
		isTaskActive,
	};
};
